import "../style/createShipment/CreateShipment.css";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";

import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";

import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormHelperText from "@mui/material/FormHelperText";
import { DynamicTitle } from "../utils/DynamicTitle";
import NotificationBar from "../components/sidebar/NotificationBar";
import { useState, useCallback } from "react";
import axios from "../api/axios";

import WrapperStyle from "../utils/WrapperStyle";
import USCustomsTitle from "../components/usCustoms/USCustomsTitle";

const USCustomsCalculator = () => {
  DynamicTitle("Amerika Gümrük Vergisi Hesaplama | iyziShip");

  const [, setHsCode] = useState("");
  const [selectedHsOption, setSelectedHsOption] = useState(null);
  const [hsOptions, setHsOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [productValue, setProductValue] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [loading] = useState(false);
  const [hsData, setHsData] = useState(null);
  const [error, setError] = useState("");
  const [calculationResult, setCalculationResult] = useState(null);
  const [selectedTariffType] = useState("general");

  // Debounced arama fonksiyonu
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchTerm) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          searchHsCodes(searchTerm);
        }, 500);
      };
    })(),
    []
  );

  // Autocomplete için arama fonksiyonu
  const searchHsCodes = async (searchTerm) => {
    if (!searchTerm || searchTerm.length <= 2) {
      setHsOptions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get(`hscode-search/${searchTerm}`);
      
      if (response.data && !response.data.error) {
        let dataToProcess = null;
        
        // Yeni API yanıt yapısını kontrol et
        if (response.data.items && Array.isArray(response.data.items)) {
          dataToProcess = response.data.items;
        } 
        // Eski formatları da destekle (geriye uyumluluk için)
        else if (response.data.status === 200 && response.data.data) {
          dataToProcess = response.data.data;
        } else if (Array.isArray(response.data)) {
          dataToProcess = response.data;
        } else if (response.data.htsno) {
          dataToProcess = response.data;
        }
        
        if (dataToProcess && Array.isArray(dataToProcess) && dataToProcess.length > 0) {
          // Autocomplete için formatla
          const formattedOptions = dataToProcess
            .filter(item => item && item.htsno && item.description) // Geçerli verileri filtrele
            .filter(item => {
              // N/A değerlerini filtrele
              const taxRate = item.general || item.special || item.other;
              return taxRate && taxRate !== "" && taxRate !== "N/A";
            })
            .map(item => ({
              label: `${item.htsno} - ${item.description}`,
              value: item.htsno,
              data: item
            }));
          
          setHsOptions(formattedOptions);
        } else {
          setHsOptions([]);
        }
      } else {
        setHsOptions([]);
      }
    } catch (error) {
      console.error("HS kodu arama hatası:", error);
      setHsOptions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // HS kodu seçildiğinde çalışacak fonksiyon
  const handleHsSelection = (selectedOption) => {
    if (!selectedOption) return;
    
    if (!productValue.trim()) {
      setError("Gönderi değeri girin");
      return;
    }

    setError("");
    const foundData = selectedOption.data;
    setHsData(foundData);
    
    // Otomatik olarak hesaplamayı da yap
    setTimeout(() => {
      handleCalculateCustoms(foundData);
    }, 100);
  };

  const handleHsCodeSearch = async () => {
    if (!selectedHsOption) {
      setError("HS kodu seçin");
      return;
    }

    if (!productValue.trim()) {
      setError("Gönderi değeri girin");
      return;
    }

    handleHsSelection(selectedHsOption);
  };

  // Değerler değiştiğinde otomatik yeniden hesaplama
  const handleRecalculate = () => {
    if (hsData && productValue.trim() && !loading) {
      handleCalculateCustoms(hsData);
    }
  };

  const handleCalculateCustoms = (dataToUse = null) => {
    const currentHsData = dataToUse || hsData;
    if (!currentHsData || !productValue) {
      setError("Önce HS kodu bulun ve mal bedelini girin");
      return;
    }

    const value = parseFloat(productValue);
    if (isNaN(value) || value <= 0) {
      setError("Geçerli bir mal bedeli girin");
      return;
    }

    // API'den gelen tarife oranlarını kullan
    let dutyRate = 0;
    let tariffSource = "Varsayılan";

    // Seçilen tarife türüne göre oranı al
    let selectedRate = "";
    if (
      selectedTariffType === "general" &&
      currentHsData.general !== undefined
    ) {
      selectedRate = currentHsData.general;
      tariffSource = "API - Genel";
    } else if (
      selectedTariffType === "special" &&
      currentHsData.special !== undefined &&
      currentHsData.special !== ""
    ) {
      selectedRate = currentHsData.special;
      tariffSource = "API - Özel";
    } else if (
      selectedTariffType === "other" &&
      currentHsData.other !== undefined &&
      currentHsData.other !== ""
    ) {
      selectedRate = currentHsData.other;
      tariffSource = "API - Diğer";
    }

    // Seçilen oranı parse et
    if (selectedRate && selectedRate !== "") {
      const rate = selectedRate.toLowerCase();
      if (rate === "free" || rate === "0%" || rate === "0") {
        dutyRate = 0;
      } else if (rate.includes("%")) {
        const parsedRate = parseFloat(rate.replace("%", ""));
        if (!isNaN(parsedRate)) {
          dutyRate = parsedRate;
        }
      } else if (!isNaN(parseFloat(rate))) {
        dutyRate = parseFloat(rate);
      }
    }

    // Gönderi adedini al
    const quantityValue = parseInt(quantity) || 1;

    // BAŞLANGIÇ TOPLAM DEĞER = Birim Değer × Adet
    const initialProductValue = value * quantityValue;

    // Hesaplamalar (başlangıç değer üzerinden)
    const dutyAmount = (initialProductValue * dutyRate) / 100; // HS Koduna Göre Vergi Tutarı
    const additionalTaxRate = 15; // %15 Ek Vergi (Tüm Türk Mallarına)
    const additionalTaxAmount = (initialProductValue * additionalTaxRate) / 100;

    // VERGİLER ÜRÜN DEĞERİNE EKLENİR
    const totalProductValueWithTaxes =
      initialProductValue + dutyAmount + additionalTaxAmount;

    // Taşıyıcı İşlem Bedeli - vergili toplam değere göre
    const carrierFeeRate = 0.05; // %5 taşıyıcı bedeli oranı
    const carrierFee = totalProductValueWithTaxes * carrierFeeRate;

    const totalTaxes = dutyAmount + additionalTaxAmount; // Toplam Vergi ve Masraflar
    const totalAmount = totalProductValueWithTaxes + carrierFee;

    setCalculationResult({
      productValue: value, // Birim değer
      quantity: quantityValue,
      initialProductValue: initialProductValue, // Başlangıç toplam değer
      dutyRate: dutyRate,
      dutyAmount: dutyAmount,
      additionalTaxRate: additionalTaxRate,
      additionalTaxAmount: additionalTaxAmount,
      totalProductValueWithTaxes: totalProductValueWithTaxes, // Vergili toplam değer
      totalTaxes: totalTaxes,
      carrierFeeRate: carrierFeeRate,
      carrierFee: carrierFee,
      totalAmount: totalAmount,
      tariffSource: tariffSource,
    });
    setError("");
  };

  return (
    <section className="createShipment">
      <NotificationBar />
      <USCustomsTitle />
      <div style={WrapperStyle()}>
                 <div
           className="createShipment-wrapper"
           style={{
             display: "flex",
             flexDirection: "column",
             alignItems: "flex-start",
             maxWidth: "1000px",
           }}
         >
          {/* Ana form */}
          <Box
            className="shipmentInfo"
            sx={{ width: "100%", maxWidth: "1000px" }}
          >
            <form className="shipmentInfo-form-wrapper">
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "var(--text)" }}
                >
                  Amerika Gümrük Vergisi Hesaplama
                </Typography>
              </Box>

              <Autocomplete
                fullWidth
                options={hsOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.label || option?.value || "";
                }}
                isOptionEqualToValue={(option, value) => {
                  return option?.value === value?.value;
                }}
                value={selectedHsOption}
                onChange={(event, newValue) => {
                  setSelectedHsOption(newValue);
                  if (newValue && productValue.trim()) {
                    handleHsSelection(newValue);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setHsCode(newInputValue);
                  debouncedSearch(newInputValue);
                }}
                loading={searchLoading}
                loadingText="Aranıyor..."
                noOptionsText="Sonuç bulunamadı"
                filterOptions={(options) => options} // Filtrelemeyi devre dışı bırak, API'den gelen sonuçları olduğu gibi göster
                sx={{ mb: 2 }}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                      <div style={{ flex: 1 }}>
                        <strong>{option.data?.htsno}</strong>
                        <br />
                        <span style={{ fontSize: '0.9em', color: '#666' }}>
                          {option.data?.description}
                        </span>
                      </div>
                      <div style={{ marginLeft: '10px', textAlign: 'right', minWidth: '60px' }}>
                        <span style={{ 
                          fontSize: '0.85em', 
                          color: '#dc3545', 
                          fontWeight: '600',
                          backgroundColor: '#fff2f2',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: '1px solid #fecaca'
                        }}>
                          {option.data?.general === 'Free' || option.data?.general === '' ? 
                            'Free' : 
                            (option.data?.general || '0%')
                          }
                        </span>
                      </div>
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="HS Code veya Ürün Adı"
                    placeholder="HS Code veya Ürün Adı ile Ara..."
                    variant="outlined"
                    size="small"
                    required
                    helperText={
                      <span style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "4px",
                        color: "var(--btnBgBlue)",
                        fontSize: "12px",
                        fontWeight: "500",
                        fontFamily: "Poppins",
                      }}>
                        <InfoOutlinedIcon sx={{ 
                          width: "14px", 
                          height: "14px", 
                          color: "var(--btnBgBlue)" 
                        }} />
                        HS Kodu 8 haneli girilmelidir
                      </span>
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Gönderi Değeri (USD)"
                  type="number"
                  value={productValue}
                  onChange={(e) => {
                    setProductValue(e.target.value);
                    // Değer değiştiğinde yeniden hesapla
                    setTimeout(() => handleRecalculate(), 500);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 280, flex: 1 }}
                  required
                />

                <TextField
                  label="Gönderi Adedi"
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    // Değer değiştiğinde yeniden hesapla
                    setTimeout(() => handleRecalculate(), 500);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 280, flex: 1 }}
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleHsCodeSearch}
                disabled={loading}
                className="iyzi-button-blue"
                fullWidth
                sx={{
                  height: "40px",
                  textTransform: "none",
                  mt: 1,
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Hesapla"
                )}
              </Button>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </form>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 2,
                mb: 2,
                py: 1,
                borderBottom: "1px solid var(--borderColor)",
              }}
            >
              {/* HS Kod Bilgileri */}
              {/* {hsData && (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "1000px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <SummarizeOutlinedIcon sx={{ color: "var(--blueBrand)" }} />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "var(--text)" }}
                    >
                      HS Kod Bilgileri
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "12px" }}
                      >
                        HS Numarası
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mt: 0.5 }}
                      >
                        {hsData.htsno}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "12px" }}
                      >
                        Açıklama
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mt: 0.5 }}
                      >
                        {hsData.description}
                      </Typography>
                    </Grid>
                    {hsData.units && hsData.units.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ fontSize: "12px" }}
                        >
                          Birim
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mt: 0.5 }}
                        >
                          {hsData.units.join(", ")}
                        </Typography>
                      </Grid>
                    )}
                    {hsData.general !== undefined && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ fontSize: "12px" }}
                        >
                          Genel Tarife Oranı
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mt: 0.5,
                            color: "var(--blueBrand)",
                          }}
                        >
                          {hsData.general === ""
                            ? "Belirtilmemiş"
                            : hsData.general}
                        </Typography>
                      </Grid>
                    )}
                    {hsData.special !== undefined && hsData.special !== "" && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ fontSize: "12px" }}
                        >
                          Özel Tarife Oranı
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mt: 0.5,
                            color: "var(--success)",
                          }}
                        >
                          {hsData.special}
                        </Typography>
                      </Grid>
                    )}
                    {hsData.other !== undefined && hsData.other !== "" && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ fontSize: "12px" }}
                        >
                          Diğer Tarife Oranı
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mt: 0.5,
                            color: "var(--warning)",
                          }}
                        >
                          {hsData.other}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )} */}

              {/* Hesaplama Sonucu */}
              {calculationResult && (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "1000px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <LocalOfferOutlinedIcon sx={{ color: "var(--success)" }} />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "var(--text)" }}
                    >
                      Hesaplama Sonucu
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {/* HS Code */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          py: 1,
                          borderBottom: "1px solid var(--borderColor)",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          HS Kodu:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: "14px" }}
                        >
                          {hsData?.htsno || "N/A"}
                        </Typography>
                      </Box>

                      {/* Ürün Adı */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          py: 1,
                          borderBottom: "1px solid var(--borderColor)",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          Ürün Adı:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            maxWidth: "200px",
                            textAlign: "right",
                          }}
                        >
                          {hsData?.description || "N/A"}
                        </Typography>
                      </Box>

                      {/* Gümrük Vergisi Oranı */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          py: 1,
                          borderBottom: "1px solid var(--borderColor)",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          Gümrük Vergisi Oranı (%):
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: "14px" }}
                        >
                          {calculationResult.dutyRate || 0}
                        </Typography>
                      </Box>

                      {/* HS Code Koduna Göre Vergi Tutarı */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          py: 1,
                          borderBottom: "1px solid var(--borderColor)",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          HS Koduna Göre Vergi Tutarı:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: "14px" }}
                        >
                          ${(calculationResult.dutyAmount || 0).toFixed(2)}
                        </Typography>
                      </Box>

                      {/* %15 Ek Vergi */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          py: 1,
                          borderBottom: "1px solid var(--borderColor)",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          %15 Ek Vergi (Tüm Türk Mallarına):
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: "14px" }}
                        >
                          $
                          {(calculationResult.additionalTaxAmount || 0).toFixed(
                            2
                          )}
                        </Typography>
                      </Box>

                      {/* Başlangıç Ürün Değeri */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          py: 1,
                          borderBottom: "1px solid var(--borderColor)",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          Başlangıç Ürün Değeri:
                        </Typography>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: "14px" }}
                          >
                            $
                            {(
                              calculationResult.initialProductValue || 0
                            ).toFixed(2)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ fontSize: "11px" }}
                          >
                            (${(calculationResult.productValue || 0).toFixed(2)}{" "}
                            × {calculationResult.quantity || 1} adet)
                          </Typography>
                        </Box>
                      </Box>

                      {/* Toplam Vergiler ve Masraflar */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                          py: 2,
                          // borderTop: "2px solid #000",
                          borderBottom: "2px solid #000",
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontSize: "16px", fontWeight: 700 }}
                        >
                          Toplam Vergiler ve Masraflar:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, fontSize: "16px" }}
                        >
                          ${(calculationResult.totalTaxes || 0).toFixed(2)} +
                          Taşıyıcı İşlem Bedeli
                        </Typography>
                      </Box>

                      {/* Taşıyıcı İşlem Bedeli */}
                      {/* <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, py: 1, borderBottom: "1px solid var(--borderColor)" }}>
                    <Typography variant="body2" sx={{ fontSize: "14px" }}>Taşıyıcı İşlem Bedeli:</Typography>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "14px" }}>
                        ${(calculationResult.carrierFee || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: "11px" }}>
                        (%{((calculationResult.carrierFeeRate || 0.05) * 100).toFixed(1)} toplam değer üzerinden)
                      </Typography>
                    </Box>
                  </Box> */}
                    </Grid>
                  </Grid>

                  {/* Taşıyıcı İşlem Bedeli Açıklaması */}
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9ecef"
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: "#495057",
                        fontSize: "14px"
                      }}
                    >
                      💡 Taşıyıcı İşlem Bedeli
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6c757d",
                        fontSize: "13px",
                        lineHeight: 1.5
                      }}
                    >
                      Taşıyıcı işlem bedeli, son taşıyıcıların aldığı gümrükleme işleme bedelidir. İyziship tarafından sunulan bir hizmet değildir.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </div>
      </div>
    </section>
  );
};

export default USCustomsCalculator;
